import { loadLanguage, preloadLanguages } from '../lib/i18n';

export interface LanguageCacheInfo {
  translationLoaded: boolean;
  gameDataLoaded: boolean;
  lastUsed: Date;
  usageCount: number;
}

export interface IntegratedLanguageCache {
  [language: string]: LanguageCacheInfo;
}

export class IntegratedLanguageService {
  private cache: IntegratedLanguageCache = {};
  private readonly MAX_LANGUAGES = 2; // Ultra-aggressive: only 2 languages (was 3)
  private readonly SUPPORTED_LANGUAGES = [
    'en', 'es', 'fr', 'hi', 'de', 'it', 'ja', 'nl', 'pt', 'ru', 'ar'
  ];

  // Global instance for console access
  private static instance: IntegratedLanguageService;

  constructor() {
    // Initialize with current language only
    this.initializeCache();
    
    // Set global instance for console access
    IntegratedLanguageService.instance = this;
    
    // Add console commands for memory optimization testing
    if (typeof window !== 'undefined') {
      (window as any).forceMemoryOptimization = () => this.forceMemoryOptimization();
      (window as any).ultraMemoryOptimization = () => this.ultraMemoryOptimization();
      (window as any).getLanguageCacheStats = () => this.getCacheStats();
      (window as any).clearLanguageCache = () => this.clearCache();
    }
  }

  /**
   * Initialize cache with current language only
   */
  private initializeCache(): void {
    const currentLang = this.getCurrentLanguage();
    this.cache[currentLang] = {
      translationLoaded: true, // Current language is pre-loaded
      gameDataLoaded: false, // Game data loaded on demand
      lastUsed: new Date(),
      usageCount: 0
    };
  }

  /**
   * Get current language from various sources
   */
  private getCurrentLanguage(): string {
    const savedLang = localStorage.getItem('language');
    if (savedLang && this.SUPPORTED_LANGUAGES.includes(savedLang)) {
      return savedLang;
    }
    
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && this.SUPPORTED_LANGUAGES.includes(browserLang)) {
      return browserLang;
    }
    
    return 'en'; // Default fallback
  }

  /**
   * Change language with lazy loading - only load when explicitly requested
   */
  async changeLanguage(newLanguage: string, currentGame?: string): Promise<void> {
    console.log(`🔄 IntegratedLanguageService: Changing to ${newLanguage}`);
    
    // Validate language
    if (!this.SUPPORTED_LANGUAGES.includes(newLanguage)) {
      console.warn(`Language ${newLanguage} not supported, falling back to English`);
      newLanguage = 'en';
    }

    try {
      // Check if we need to clean up old languages
      this.enforceLanguageLimit(newLanguage);

      // Load translation if not already loaded (lazy loading)
      if (!this.cache[newLanguage]?.translationLoaded) {
        console.log(`📚 Loading ${newLanguage} translations on demand...`);
        await loadLanguage(newLanguage);
        this.updateCache(newLanguage, 'translationLoaded', true);
        console.log(`✅ ${newLanguage} translations loaded and cached`);
      } else {
        console.log(`📚 ${newLanguage} translations already loaded`);
      }

      // Game data is now loaded from CDN, no need to preload here
      // CDN-based GameQuestionManager handles all question loading
      if (currentGame) {
        console.log(`🎮 Game data for ${currentGame} will be loaded from CDN when needed`);
        this.updateCache(newLanguage, 'gameDataLoaded', true);
      }

      // Update cache info
      this.updateCache(newLanguage, 'lastUsed', new Date());
      this.updateCache(newLanguage, 'usageCount', (this.cache[newLanguage]?.usageCount || 0) + 1);

      console.log(`✅ Successfully changed to ${newLanguage}`);
      console.log(`📊 Cache status:`, this.getCacheStats());
      console.log(`🔍 i18n language after change:`, (await import('../lib/i18n')).default.language);

    } catch (error) {
      console.error(`❌ Error changing language to ${newLanguage}:`, error);
      throw error;
    }
  }

  /**
   * Load game data for a specific game and language (lazy loading)
   * Note: Game data is now loaded from CDN by GameQuestionManager
   */
  async loadGameData(gameType: string, language: string): Promise<void> {
    if (!this.SUPPORTED_LANGUAGES.includes(language)) {
      throw new Error(`Unsupported language: ${language}`);
    }

    try {
      // Ensure language is in cache (load translation if needed)
      if (!this.cache[language]) {
        await this.changeLanguage(language);
      }

      // Game data is now loaded from CDN, just mark as loaded
      if (!this.cache[language]?.gameDataLoaded) {
        console.log(`🎮 Game data for ${gameType} in ${language} will be loaded from CDN when needed`);
        this.updateCache(language, 'gameDataLoaded', true);
        this.updateCache(language, 'lastUsed', new Date());
      } else {
        console.log(`🎮 Game data already marked as loaded for ${gameType} in ${language}`);
      }

    } catch (error) {
      console.error(`❌ Error setting up game data for ${gameType} in ${language}:`, error);
      throw error;
    }
  }

  /**
   * Preload languages in background - DISABLED for 24+ languages
   * Only load when explicitly requested to save memory and network
   */
  async preloadLanguages(languages: string[]): Promise<void> {
    // DISABLED: Don't preload for 24+ languages to save resources
    console.log(`🚫 Preloading disabled for 24+ language support. Languages will be loaded on demand.`);
    
    // Only preload if user explicitly requests it (future feature)
    if (languages.length === 0) {
      return;
    }
    
    // For now, just log what would be preloaded
    const languagesToPreload = languages
      .filter(lang => this.SUPPORTED_LANGUAGES.includes(lang))
      .slice(0, 2); // Very limited preloading

    if (languagesToPreload.length > 0) {
      console.log(`🔄 Limited preloading: ${languagesToPreload.join(', ')}`);
      
      // Only preload translations, not game data
      await preloadLanguages(languagesToPreload);
    }
  }

  /**
   * Enforce 3-language limit using aggressive LRU strategy
   */
  private enforceLanguageLimit(newLanguage: string): void {
    const currentLanguages = Object.keys(this.cache);
    
    // If adding new language would exceed limit, remove oldest
    if (currentLanguages.length >= this.MAX_LANGUAGES && !this.cache[newLanguage]) {
      this.removeOldestLanguage();
    }
    
    // Additional cleanup: if we have 3+ languages, always keep only 2 + current
    if (currentLanguages.length >= 3) {
      this.aggressiveCleanup(newLanguage);
    }
  }

  /**
   * Remove the oldest language from cache
   */
  private removeOldestLanguage(): void {
    const languages = Object.keys(this.cache);
    if (languages.length === 0) return;

    // Find oldest language (lowest lastUsed timestamp)
    const oldestLanguage = languages.reduce((oldest, current) => {
      const oldestTime = this.cache[oldest].lastUsed.getTime();
      const currentTime = this.cache[current].lastUsed.getTime();
      return currentTime < oldestTime ? current : oldest;
    });

    // Don't remove English if it's the only language
    if (oldestLanguage === 'en' && languages.length === 1) {
      return;
    }

    // Don't remove the current language
    const currentLanguage = this.getCurrentLanguage();
    if (oldestLanguage === currentLanguage) {
      return;
    }

    console.log(`🗑️ Removing oldest language from cache: ${oldestLanguage}`);
    
    // Remove from integrated cache
    delete this.cache[oldestLanguage];
    
    // Game data is now handled by CDN, no need to clear cache
    
    // Note: Translation cache is handled by i18n library
    // We rely on the existing 2-language limit in i18n.ts
  }

  /**
   * Aggressive cleanup to keep only 2 languages + current
   */
  private aggressiveCleanup(newLanguage: string): void {
    const languages = Object.keys(this.cache);
    if (languages.length < 3) return;

    // Sort languages by last used time (oldest first)
    const sortedLanguages = languages.sort((a, b) => {
      const timeA = this.cache[a].lastUsed.getTime();
      const timeB = this.cache[b].lastUsed.getTime();
      return timeA - timeB;
    });

    // Keep only the 2 most recently used languages + the new language
    const languagesToKeep = new Set([newLanguage, 'en']); // Always keep English
    
    // Add the most recently used language
    for (let i = sortedLanguages.length - 1; i >= 0; i--) {
      const lang = sortedLanguages[i];
      if (!languagesToKeep.has(lang)) {
        languagesToKeep.add(lang);
        break;
      }
    }

    // Remove languages not in the keep list
    for (const lang of languages) {
      if (!languagesToKeep.has(lang)) {
        console.log(`🗑️ Aggressive cleanup: Removing ${lang} from cache`);
        delete this.cache[lang];
        
        // Game data is now handled by CDN, no need to clear cache
      }
    }
  }

  /**
   * Update cache information
   */
  private updateCache(language: string, key: keyof LanguageCacheInfo, value: any): void {
    if (!this.cache[language]) {
      this.cache[language] = {
        translationLoaded: false,
        gameDataLoaded: false,
        lastUsed: new Date(),
        usageCount: 0
      };
    }
    
    (this.cache[language] as any)[key] = value;
  }

  /**
   * Get cache statistics with memory optimization info
   */
  getCacheStats(): {
    totalLanguages: number;
    languages: string[];
    memoryUsage: number;
    optimizationInfo: {
      maxLanguages: number;
      cacheEfficiency: string;
      memorySavings: string;
      memoryUsageMB?: string;
    };
  } {
    const languages = Object.keys(this.cache);
    // Game data is now handled by CDN, no need for game data stats
    
    // Calculate memory optimization metrics
    const cacheEfficiency = languages.length <= this.MAX_LANGUAGES ? 'Optimal' : 'Over Limit';
    const memorySavings = languages.length <= 2 ? 'Ultra High' : languages.length <= 3 ? 'High' : 'Low';
    
    // Estimate memory usage in MB
    let memoryUsageMB = 'Unknown';
    if (typeof performance !== 'undefined' && (performance as any).memory) {
      const memory = (performance as any).memory;
      const usedMB = Math.round(memory.usedJSHeapSize / 1024 / 1024);
      memoryUsageMB = `${usedMB}MB`;
    }
    
    return {
      totalLanguages: languages.length,
      languages,
      memoryUsage: JSON.stringify(this.cache).length,

      optimizationInfo: {
        maxLanguages: this.MAX_LANGUAGES,
        cacheEfficiency,
        memorySavings,
        memoryUsageMB
      }
    };
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache = {};
    console.log('🧹 Cleared all language cache');
  }

  /**
   * Force memory optimization - aggressive cleanup
   */
  forceMemoryOptimization(): void {
    console.log('🔧 Forcing memory optimization...');
    
    // Keep only current language and English
    const currentLanguage = this.getCurrentLanguage();
    const languagesToKeep = new Set([currentLanguage, 'en']);
    
    // Remove all other languages
    const languagesToRemove = Object.keys(this.cache).filter(lang => !languagesToKeep.has(lang));
    
    for (const lang of languagesToRemove) {
      console.log(`🗑️ Force cleanup: Removing ${lang}`);
      delete this.cache[lang];
    }
    
    // Game data is now handled by CDN, no need to clear cache
    
    // Force garbage collection if available
    if (typeof window !== 'undefined' && (window as any).gc) {
      (window as any).gc();
      console.log('🗑️ Forced garbage collection');
    }
    
    console.log(`✅ Memory optimization complete. Kept: ${Array.from(languagesToKeep).join(', ')}`);
  }

  /**
   * Ultra-aggressive memory optimization - keep only current language
   */
  ultraMemoryOptimization(): void {
    console.log('🚀 Ultra-aggressive memory optimization...');
    
    // Keep only current language
    const currentLanguage = this.getCurrentLanguage();
    const languagesToRemove = Object.keys(this.cache).filter(lang => lang !== currentLanguage);
    
    for (const lang of languagesToRemove) {
      console.log(`🗑️ Ultra cleanup: Removing ${lang}`);
      delete this.cache[lang];
    }
    
    // Game data is now handled by CDN, no need to clear cache
    
    // Force garbage collection if available
    if (typeof window !== 'undefined' && (window as any).gc) {
      (window as any).gc();
      console.log('🗑️ Forced garbage collection');
    }
    
    console.log(`✅ Ultra memory optimization complete. Kept only: ${currentLanguage}`);
  }

  /**
   * Get supported languages
   */
  getSupportedLanguages(): string[] {
    return [...this.SUPPORTED_LANGUAGES];
  }

  /**
   * Check if language is supported
   */
  isLanguageSupported(language: string): boolean {
    return this.SUPPORTED_LANGUAGES.includes(language);
  }

  /**
   * Get current cache status
   */
  getCurrentCacheStatus(): IntegratedLanguageCache {
    return { ...this.cache };
  }
}

// Export singleton instance
export const integratedLanguageService = new IntegratedLanguageService(); 